import React, { useState } from "react";
import { motion } from "framer-motion";
import { Building2, HeartHandshake, Quote } from "lucide-react";
import { Container, Reveal, Overline } from "../components/common";
import { PageHero } from "../components/PageHero";
import { IMAGES } from "../lib/content";
import { DEFAULT_PARTNERS, groupPartnersByType } from "../lib/partnerLogos";
import { fetchPartners } from "../lib/api";
import { useApiData } from "../hooks/useApiData";

const initials = (name) =>
  name
    .split(" ")
    .filter((w) => /[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export default function Partners() {
  const [active, setActive] = useState("corporate");
  const partners = useApiData(fetchPartners, DEFAULT_PARTNERS, groupPartnersByType);

  const TABS = [
    {
      id: "corporate",
      label: "Corporate Partners",
      icon: Building2,
      intro:
        "We extend our deepest gratitude to the corporate organizations whose generous partnerships and continued support make our grassroots mission possible.",
      list: partners.corporate,
    },
    {
      id: "ngo",
      label: "NGO Partners",
      icon: HeartHandshake,
      intro:
        "Together with trusted NGO and institutional partners, we extend our reach and multiply our impact across communities in Telangana & Andhra Pradesh, and increasingly across India.",
      list: partners.ngo,
    },
  ];
  const tab = TABS.find((t) => t.id === active);

  return (
    <>
      <PageHero
        overline="Our Partners"
        title="Collaborating for grassroots transformation"
        subtitle="Real change is never a solo act. These partners walk with us — funding, guiding and amplifying our work."
        image={IMAGES.volunteers}
      />

      <section className="py-20 lg:py-28 bg-white" data-testid="partners-tabs-section">
        <Container>
          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1.5 rounded-full bg-teal-50 border border-teal-100" role="tablist" data-testid="partners-tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  onClick={() => setActive(t.id)}
                  data-testid={`partners-tab-${t.id}`}
                  className={`relative inline-flex items-center gap-2 rounded-full px-5 sm:px-7 py-3 text-sm font-semibold transition-colors ${
                    active === t.id ? "bg-teal-700 text-white" : "text-teal-700 hover:text-teal-900"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={tab.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="max-w-3xl mx-auto text-center mb-12">
              <Quote className="h-9 w-9 text-coral mx-auto" />
              <p className="mt-4 font-serif text-2xl sm:text-3xl italic text-navy leading-snug text-balance">
                {tab.intro}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`partners-grid-${tab.id}`}>
              {tab.list.map((p, i) => (
                <motion.div
                  key={p.id || p.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="group flex items-center gap-5 rounded-3xl bg-offwhite border border-slate-100 p-6 hover:border-teal-200 hover:bg-white hover:shadow-card transition-all"
                  data-testid={`partner-card-${tab.id}-${i}`}
                >
                  {p.logo_path ? (
                    <div className="grid place-items-center h-16 w-16 shrink-0 rounded-2xl bg-white border border-slate-100 overflow-hidden p-1.5">
                      <img src={p.logo_path} alt={`${p.name} logo`} className="h-full w-full object-contain" />
                    </div>
                  ) : (
                    <div className="grid place-items-center h-16 w-16 shrink-0 rounded-2xl bg-teal-700 text-white font-display font-extrabold text-xl group-hover:bg-coral transition-colors">
                      {initials(p.name)}
                    </div>
                  )}
                  <div>
                    <div className="font-display font-bold text-navy leading-snug">{p.name}</div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mt-1">
                      {tab.id === "corporate" ? "CSR Partner" : "NGO Partner"}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-3xl bg-teal-900 p-10 lg:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-[0.07]" />
              <h3 className="relative font-display text-2xl sm:text-3xl font-extrabold text-white">
                Want to partner with Janma Foundation?
              </h3>
              <p className="relative mt-3 text-teal-100 max-w-xl mx-auto">
                Join hands with us through CSR funding, in-kind support or collaborative programs.
              </p>
              <a
                href="/get-involved"
                data-testid="partners-cta-button"
                className="relative mt-7 inline-flex items-center gap-2 rounded-full bg-coral px-7 py-4 text-base font-semibold text-white hover:bg-coral-700 transition-colors"
              >
                Start a Partnership
              </a>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
